import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VerifiedBadge } from "@/components/Badges";
import { toast } from "sonner";
import { CREATOR_TYPES as PRO_TYPES, labelCreatorType } from "@/lib/roleCategories";
import CreatorGalleryEditor from "@/components/CreatorGalleryEditor";

const COMPANY_SIZES = [
  { value: "solo", label: "Indépendant" },
  { value: "2-10", label: "2-10 personnes" },
  { value: "11-50", label: "11-50 personnes" },
  { value: "51-200", label: "51-200 personnes" },
  { value: "200+", label: "200+ personnes" },
];

const INDUSTRIES = [
  "Prêt-à-porter",
  "Haute couture",
  "Beauté & cosmétiques",
  "Bijouterie",
  "Lunetterie",
  "Maroquinerie",
  "Parfum",
  "Accessoires",
  "Lingerie",
  "Streetwear",
  "Autre",
];

export default function ProProfile() {
  const { user, profile, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "",
    pro_type: "",
    description: "",
    location: "",
    logo_url: "",
    website: "",
    instagram: "",
    industry: "",
    company_size: "",
    project_types: [],
  });

  useEffect(() => {
    if (!loading && user && (user.role !== "creator" && user.role !== "professional")) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (profile) {
      setForm({
        company_name: profile.company_name || "",
        pro_type: profile.pro_type || "",
        description: profile.description || "",
        location: profile.location || "",
        logo_url: profile.logo_url || "",
        website: profile.website || "",
        instagram: profile.instagram || "",
        industry: profile.industry || "",
        company_size: profile.company_size || "",
        project_types: profile.project_types || [],
      });
    }
  }, [profile]);

  const handleSave = async () => {
    await api.put("/pros/me", form);
    await checkAuth();
    toast.success("Profil mis à jour");
  };

  const toggleProjectType = (v) => {
    setForm((f) => ({
      ...f,
      project_types: f.project_types.includes(v)
        ? f.project_types.filter((x) => x !== v)
        : [...f.project_types, v],
    }));
  };

  if (!user || !profile) return null;

  const PROJECT_OPTIONS = ["Shooting", "Casting", "Défilé", "Campagne", "Éditorial", "Lookbook", "Beauté", "Événement"];

  return (
    <AppShell>
      <div data-testid="pro-profile-page">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="caption-label text-[#C68E59]">Profil marque</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1">Votre identité</h1>
          </div>
          {profile.verified && <VerifiedBadge />}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-[#EADDC9] p-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="caption-label">Nom de la marque</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-xl mt-2" data-testid="brand-name-input" />
              </div>
              <div>
                <Label className="caption-label">Type</Label>
                <Select value={form.pro_type} onValueChange={(v) => setForm({ ...form, pro_type: v })}>
                  <SelectTrigger className="rounded-xl mt-2"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {PRO_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption-label">Localisation</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl mt-2" placeholder="Paris, France" />
              </div>
              <div>
                <Label className="caption-label">Secteur</Label>
                <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                  <SelectTrigger className="rounded-xl mt-2"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption-label">Taille</Label>
                <Select value={form.company_size} onValueChange={(v) => setForm({ ...form, company_size: v })}>
                  <SelectTrigger className="rounded-xl mt-2"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {COMPANY_SIZES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption-label">Site web</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="rounded-xl mt-2" placeholder="https://…" />
              </div>
              <div>
                <Label className="caption-label">Instagram</Label>
                <Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="rounded-xl mt-2" placeholder="@votre_marque" />
              </div>
              <div>
                <Label className="caption-label">Logo (URL)</Label>
                <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="rounded-xl mt-2" placeholder="https://…" />
              </div>
            </div>

            <div>
              <Label className="caption-label">Bio / Description</Label>
              <Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-2" placeholder="Présentez votre marque, votre ADN, vos projets…" />
            </div>

            <div>
              <Label className="caption-label mb-2 block">Types de projets</Label>
              <div className="flex flex-wrap gap-2">
                {PROJECT_OPTIONS.map((p) => {
                  const active = form.project_types.includes(p);
                  return (
                    <button
                      type="button"
                      key={p}
                      onClick={() => toggleProjectType(p)}
                      className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition ${
                        active
                          ? "bg-[#C68E59] border-[#C68E59] text-white"
                          : "bg-white border-[#D4C3A5] text-[#666] hover:border-[#1A1A1A]"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            <Button onClick={handleSave} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="save-brand-profile">
              Enregistrer
            </Button>

            <div className="pt-6 border-t border-[#EADDC9]">
              <CreatorGalleryEditor initialGallery={profile.gallery_images || []} />
            </div>
          </div>

          {/* Preview card */}
          <div className="bg-white rounded-2xl border border-[#EADDC9] p-6 h-fit">
            <p className="caption-label text-[#8B8680]">Aperçu</p>
            {form.logo_url ? (
              <img src={form.logo_url} alt="" className="w-20 h-20 rounded-full object-cover mt-3 border border-[#EADDC9]" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#F5E6D0] flex items-center justify-center text-2xl font-serif text-[#1A1A1A] mt-3">
                {(form.company_name || "M")[0]?.toUpperCase()}
              </div>
            )}
            <p className="font-serif text-2xl text-[#1A1A1A] mt-3">{form.company_name || "Nom de la marque"}</p>
            <p className="text-xs text-[#8B8680]">{labelCreatorType(form.pro_type) || "—"} {form.industry && `• ${form.industry}`}</p>
            {form.location && <p className="text-xs text-[#666] mt-1">{form.location}</p>}
            {form.description && <p className="text-sm text-[#666] mt-4 leading-relaxed line-clamp-4">{form.description}</p>}
            <div className="flex flex-wrap gap-2 mt-4">
              {form.project_types.map((p) => (
                <span key={p} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#F5E6D0] text-[#8B5A2B]">{p}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
